import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CustomPatterns, CustomValidators } from '@cargos/sprintpay-utils';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { InvalidFieldComponent } from 'src/app/shared/components';
import { CustomerService, SessionService } from 'src/app/shared/services';
import { AutoPayFormGroupType } from '../auto-pay/models/auto-pay-form';
import { AwbSubscriptionService } from '../services/awb-subscription.service';
import { MAWBSubscriptionFormErrorJSON, MAWBSubscriptionFormGroupType } from './models/mawb-subscription-form';
import { MAWBSubscriptionFormErrors } from './utils/form-errors';
@Component({
    selector: 'app-mawb-subscription',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        MatCheckboxModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        InvalidFieldComponent,
    ],
    templateUrl: './mawb-subscription.component.html',
    styleUrl: './mawb-subscription.component.scss',
})
export class MawbSubscriptionComponent implements OnInit, OnDestroy, AfterViewInit {
    public showEmail: boolean = false;
    public mawbSubscriptionForm: MAWBSubscriptionFormGroupType;
    private unsubscribe$: Subject<void>;
    public formErrors: MAWBSubscriptionFormErrorJSON;
    private isSelectedAutoPayFirst: boolean;
    private isArrivalSubscriptionExist = false;
    private isAuthenticated = false;
    private autoPayForm: AutoPayFormGroupType;

    get email(): AbstractControl<string> | null {
        return this.mawbSubscriptionForm.get('email');
    }

    get isSelected(): AbstractControl<boolean> | null {
        return this.mawbSubscriptionForm.get('isSelected');
    }

    constructor(
        private awbSubscriptionService: AwbSubscriptionService,
        private fb: NonNullableFormBuilder,
        private customerService: CustomerService,
        private sessionService: SessionService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.formErrors = MAWBSubscriptionFormErrors;
        this.isArrivalSubscriptionExist = this.awbSubscriptionService.getIsArrivalSubscriptionExist();
    }

    ngOnInit(): void {
        this.subscribeIsUserAuthenticated();
        this.createForm();
        this.initEmail();
    }

    ngAfterViewInit(): void {
        this.subscribeAutoPay();
        this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createForm(): void {
        this.mawbSubscriptionForm = this.fb.group({
            isSelected: [this.isArrivalSubscriptionExist],
            email: ['', [Validators.required, CustomValidators.regex(new RegExp(CustomPatterns.EMAIL))]],
        });
        this.awbSubscriptionService.setSubscriptionForm(this.mawbSubscriptionForm);
    }

    initEmail(): void {
        this.setEmail();
        if (this.isArrivalSubscriptionExist) {
            this.showEmail = true;
        }
    }

    onKeyEmail(): void {
        this.awbSubscriptionService.setSubscriptionForm(this.mawbSubscriptionForm);
    }

    activeSubscription(isSelected: boolean): void {
        if (
            this.isArrivalSubscriptionExist ||
            this.isSelectedAutoPayFirst ||
            (!isSelected && this.autoPayForm.get('isSelected')?.value && this.autoPayForm.get('email')?.valid)
        ) {
            this.isSelected?.setValue(true);
            return;
        }
        this.showEmail = isSelected;
        if (isSelected) {
            this.setEmail();
        } else {
            this.email?.clearValidators();
        }
        this.mawbSubscriptionForm.updateValueAndValidity();
        this.awbSubscriptionService.setSubscriptionForm(this.mawbSubscriptionForm);
    }

    subscribeAutoPay(): void {
        combineLatest([
            this.awbSubscriptionService.getAutoPayForm(),
            this.awbSubscriptionService.isSelectAutoPayFirst(),
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: ([autoPayForm, isSelectedAutoPayFirst]) => {
                    this.autoPayForm = autoPayForm;
                    this.isSelectedAutoPayFirst = isSelectedAutoPayFirst;
                    if (!(this.email?.value && this.isSelected?.value)) {
                        this.showEmail = false;
                        this.email?.setErrors(null);
                    }
                },
            });
    }

    subscribeIsUserAuthenticated(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isAuthenticated) => (this.isAuthenticated = isAuthenticated));
    }

    setEmail(): void {
        if (this.isAuthenticated) {
            const email = this.customerService.getCustomerEmail();
            this.email?.setValue(email);
        }
    }
}
